import { ApplicationConfig, inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { appRoutes } from './app.routes';
import { provideMapboxGL } from 'ngx-mapbox-gl';
import { environment } from 'src/environments/environment';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { providePrimeNG } from 'primeng/config';
import MyPreset from './theme/app-theme';
import {
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalInterceptor,
    MsalGuardConfiguration,
    MsalInterceptorConfiguration,
} from '@azure/msal-angular';
import { ErrorInterceptor } from '@core/interceptors/error.interceptor';
import {
    BrowserCacheLocation,
    ClientConfigurationError,
    InteractionRequiredAuthError,
    InteractionType,
    IPublicClientApplication,
    LogLevel,
    PublicClientApplication,
} from '@azure/msal-browser';
import { apiConfig, b2cPolicies } from './b2c-config';

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

export function loggerCallback(logLevel: LogLevel, message: string) {
    console.log(`[MSAL] ${message}`);
}

export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication({
        auth: {
            clientId: environment.b2cClientId,
            authority: b2cPolicies.authorities.signUpSignIn.authority,
            redirectUri: '/',
            postLogoutRedirectUri: '/',
            knownAuthorities: [b2cPolicies.authorityDomain],
            navigateToLoginRequestUrl: false,
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
            storeAuthStateInCookie: isIE, // set to true for IE 11
        },
        system: {
            loggerOptions: {
                loggerCallback,
                logLevel: LogLevel.Warning,
                piiLoggingEnabled: false,
            },
        },
    });
}
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();
    protectedResourceMap.set(apiConfig.uri, apiConfig.scopes);

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap,
    };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: [...apiConfig.scopes],
        },
        loginFailedRoute: '/login',
    };
}

async function initializeMsalSession(msalService: MsalService): Promise<void> {
    if (window !== window.parent && !window.opener) return;

    const instance = msalService.instance;
    await instance.initialize();

    const redirectResult = await instance.handleRedirectPromise();
    if (redirectResult?.account) {
        instance.setActiveAccount(redirectResult.account);
    }

    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) return;

    instance.setActiveAccount(account);

    try {
        await instance.acquireTokenSilent({ account, scopes: [...apiConfig.scopes] });
    } catch (error) {
        if (
            error instanceof InteractionRequiredAuthError ||
            (error instanceof ClientConfigurationError && error.errorCode === 'authority_mismatch')
        ) {
            await instance.clearCache();
            return;
        }

        throw error;
    }
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideAppInitializer(() => {
            const msalService = inject(MsalService);
            inject(MsalBroadcastService);
            return initializeMsalSession(msalService);
        }),
        provideZonelessChangeDetection(),
        provideRouter(appRoutes),
        provideHttpClient(withInterceptorsFromDi()),
        provideMapboxGL({
            accessToken: environment.mapboxAccessToken,
        }),
        providePrimeNG({
            license:
                'eyJpZCI6IjQ3MWY5Y2QwLTViNjktNGZmMC1hODJmLWQxZjNhOWMyYTQ5NSIsInByb2R1Y3QiOiJwcmltZXVpIiwidGllciI6ImNvbW11bml0eSIsInR5cGUiOiJkZXYiLCJpYXQiOjE3OTAzNzUwNTIsImV4cCI6MTgyMTkxMTA1Mn0.pKGfMsk5e8mYWPTg7phUSn9ZgsRRyEEQgpMElupguAugyeazisbua6hMWdO5iagSyOMLOijOCbEPad5LMobvCA',
            theme: {
                preset: MyPreset,
                options: {
                    darkModeSelector: '.theme-dark',
                },
            },
        }),
        provideStoreDevtools({
            maxAge: 25, // Retains last 25 states
            autoPause: true, // Pauses recording actions and state changes when the extension window is not open
            logOnly: environment.production,
            traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
            connectInZone: true,
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
    ],
};
